import { AsyncPipe, NgClass, NgTemplateOutlet, PercentPipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  Input,
  OnInit,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { isEqual } from 'lodash-es';
import { distinctUntilChanged, map, tap } from 'rxjs/operators';

import { InputComponent } from '@shared/components/input/input.component';
import { InputMaskComponent } from '@shared/components/input-mask/input-mask.component';
import { VarDirective } from '@shared/directives/var.directive';
import { MoneyPipe } from '@shared/pipes/money.pipe';
import { decimalDifference, decimalRoundingToNumber } from '@shared/utils';

import { InvestigatorAveragesService, InvestigatorForecastService } from '../../../../services';
import { InvestigatorForecastForm } from '../../../../types/investigator-forecast.types';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    VarDirective,
    NgTemplateOutlet,
    FormsModule,
    ReactiveFormsModule,
    NgClass,
    InputComponent,
    InputMaskComponent,
    MoneyPipe,
    AsyncPipe,
    PercentPipe,
  ],
  selector: 'aux-investigator-forecast-overview-averages',
  styles: [
    `
      ::ng-deep .averages-form input:not([type='radio']) {
        color: black;
      }
    `,
  ],
  templateUrl: './overview-averages.component.html',
})
export class InvestigatorForecastOverviewAveragesComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);
  private readonly investigatorAveragesService = inject(InvestigatorAveragesService);
  private readonly investigatorForecastService = inject(InvestigatorForecastService);

  averagesValues$ = this.investigatorForecastService.averagesValues$;
  editMode$ = this.investigatorAveragesService.editMode$;
  @Input() form!: FormGroup<InvestigatorForecastForm>;
  formFieldLabels$ = this.averagesValues$.pipe(
    map((values) => {
      const { current_average_patient_cost, total_forecasted_patients } =
        values.find(({ last_updated }) => last_updated === 'Current') || {};

      return {
        averagePatientCostPerSite: current_average_patient_cost,
        totalForecastedPatients: total_forecasted_patients,
      };
    })
  );

  private maxPercent = 100;

  ngOnInit(): void {
    this.recalculateAverageForm();
    this.roundInvoiceablesField('otherInvoiceablesPerc', 'overheadInvoiceablesPerc');
    this.roundInvoiceablesField('overheadInvoiceablesPerc', 'otherInvoiceablesPerc');
    this.updateAutoPopulateFieldsOnSiteChange();
  }

  getSiteContract(contractedSitesDate: string) {
    return (
      this.averagesValues$
        .getValue()
        .find(({ last_updated }) => last_updated === contractedSitesDate)?.site_count || 0
    );
  }

  private recalculateAverageForm() {
    const formGroup = this.form.controls.averages;

    formGroup?.valueChanges
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        distinctUntilChanged(isEqual),
        tap((values) => {
          if (this.investigatorForecastService.isLoadingSettings$.getValue()) {
            return;
          }

          const forecastedVisitCosts = this.investigatorAveragesService.getForecastedVisitCost(
            values.totalForecastedPatients,
            values.averagePatientCost
          );

          const forecastedInvoiceables = this.investigatorAveragesService.getForecastedInvoiceables(
            forecastedVisitCosts,
            values.invoiceables
          );

          const totalInvestigatorCosts = this.investigatorAveragesService.getTotalInvestigatorCost(
            forecastedVisitCosts,
            forecastedInvoiceables
          );

          const remainingAmount = this.investigatorAveragesService.getRemainingAmount(
            totalInvestigatorCosts,
            this.form.get('contracted')?.value.spendToDateAmount || 0
          );

          formGroup.patchValue(
            {
              forecastedInvoiceables,
              forecastedVisitCosts,
              remainingAmount,
              totalInvestigatorCosts,
            },
            { emitEvent: false }
          );
        })
      )
      .subscribe();
  }

  private roundInvoiceablesField(formControlName: string, dependingControlName: string) {
    const formControl = this.form.get(['averages', formControlName]);
    const dependingControl = this.form.get(['averages', dependingControlName]);

    if (formControl) {
      formControl.valueChanges
        .pipe(
          takeUntilDestroyed(this.destroyRef),
          distinctUntilChanged(isEqual),
          tap((percentage: number) => {
            const newVal = decimalDifference(this.maxPercent, percentage, 2);

            dependingControl?.patchValue(newVal, { emitEvent: false });
          })
        )
        .subscribe();
    }
  }

  private updateAutoPopulateFieldsOnSiteChange() {
    this.form
      .get(['averages', 'contractedSites'])
      ?.valueChanges.pipe(
        takeUntilDestroyed(this.destroyRef),
        tap((contractedSiteDate) => {
          const averages = this.averagesValues$
            .getValue()
            .find(({ last_updated }) => last_updated === contractedSiteDate);

          if (averages) {
            const { average_patient_cost, current_average_patient_cost } = averages;

            const averagePatientCost =
              averages.last_updated === 'Current'
                ? current_average_patient_cost
                : average_patient_cost;

            this.form.controls.averages.patchValue({
              averagePatientCost: decimalRoundingToNumber(averagePatientCost),
              totalForecastedPatients: averages.total_forecasted_patients,
            });
          }
        })
      )
      .subscribe();
  }
}
